import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, DataTable } from "../components/Shell";
import DeleteButton from "../components/DeleteButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DatePicker } from "../components/DatePicker";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { FloppyDisk, CreditCard, Money, Receipt, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function monthBounds(year, month) {
  const start = new Date(year, month - 1, 1).toISOString().slice(0, 10);
  const end = new Date(year, month, 0).toISOString().slice(0, 10);
  return { start, end };
}

export default function DailyCash() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState({
    date_jour: todayISO(),
    cb_total: 0,
    cash_total: 0,
    charges_total: 0,
    commentaire: "",
  });
  const [saving, setSaving] = useState(false);
  const [monthly, setMonthly] = useState(null);
  const [existingForDate, setExistingForDate] = useState(null);

  const now = new Date();
  const [period, setPeriod] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });

  const ca = useMemo(() => (Number(f.cb_total) || 0) + (Number(f.cash_total) || 0), [f.cb_total, f.cash_total]);
  const net = useMemo(() => ca - (Number(f.charges_total) || 0), [ca, f.charges_total]);

  const loadExisting = async (date_jour) => {
    const r = await api.get(`/daily-cash/by-date/${date_jour}`);
    if (r.data) {
      setExistingForDate(r.data);
      setF({
        date_jour: r.data.date_jour,
        cb_total: r.data.cb_total,
        cash_total: r.data.cash_total,
        charges_total: r.data.charges_total,
        commentaire: r.data.commentaire || "",
      });
    } else {
      setExistingForDate(null);
      setF((prev) => ({ ...prev, date_jour, cb_total: 0, cash_total: 0, charges_total: 0, commentaire: "" }));
    }
  };

  const loadMonthly = () =>
    api.get("/daily-cash/summary/monthly", { params: period }).then((r) => setMonthly(r.data));

  useEffect(() => {
    loadExisting(todayISO());
  }, []);
  useEffect(() => {
    if (isAdmin) loadMonthly();
    // eslint-disable-next-line
  }, [period, isAdmin]);

  const onDateChange = (v) => {
    setF({ ...f, date_jour: v });
    loadExisting(v);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/daily-cash", f);
      toast.success(existingForDate ? "Compte du jour mis à jour — email envoyé au patron" : "Compte du jour enregistré — email envoyé au patron");
      loadExisting(f.date_jour);
      if (isAdmin) loadMonthly();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const setToday = () => {
    const t = todayISO();
    setF({ ...f, date_jour: t });
    loadExisting(t);
  };

  const monthLabel = new Date(period.year, period.month - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  return (
    <div>
      <PageHeader
        testId="daily-cash-header"
        title="Compte du jour"
        subtitle="Saisie rapide de la caisse : CB, espèces, charges. CA et net calculés automatiquement."
      />

      {/* Grande saisie */}
      <div className="surface p-5 md:p-6 mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <CalendarBlank size={20} color="#fbbf24" />
            <DatePicker
              testId="cash-date"
              value={f.date_jour}
              onChange={(v) => onDateChange(v)}
              className="h-12 w-[200px]"
            />
            <Button
              variant="outline"
              onClick={setToday}
              className="h-12 border-[#27272a] text-white hover:bg-[#1a1a21]"
              data-testid="btn-today"
            >
              Aujourd'hui
            </Button>
          </div>
          {existingForDate && (
            <span className="text-xs" style={{ color: "#fbbf24" }}>
              ⚠ Un compte existe déjà pour cette date — il sera mis à jour
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <BigNumericInput
            testId="cash-cb"
            icon={CreditCard}
            color="#60a5fa"
            label="CB total"
            hint="Paiements par carte"
            value={f.cb_total}
            onChange={(v) => setF({ ...f, cb_total: v })}
          />
          <BigNumericInput
            testId="cash-espece"
            icon={Money}
            color="#34d399"
            label="Espèces total"
            hint="Paiements en liquide"
            value={f.cash_total}
            onChange={(v) => setF({ ...f, cash_total: v })}
          />
          <BigNumericInput
            testId="cash-charges"
            icon={Receipt}
            color="#f87171"
            label="Total charges"
            hint="Dépenses de la soirée"
            value={f.charges_total}
            onChange={(v) => setF({ ...f, charges_total: v })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="surface p-4 !bg-[#09090b]">
            <div className="label-xs">CA du jour</div>
            <div className="text-xs text-[#71717a] mb-2">CB + espèces</div>
            <div data-testid="ca-preview" className="font-display text-3xl md:text-4xl font-light" style={{ color: "#fbbf24" }}>
              {fmtMoney(ca)}
            </div>
          </div>
          <div className="surface p-4 !bg-[#09090b]">
            <div className="label-xs">Net du jour</div>
            <div className="text-xs text-[#71717a] mb-2">CA − charges</div>
            <div data-testid="net-preview" className="font-display text-3xl md:text-4xl font-light" style={{ color: net >= 0 ? "#34d399" : "#f87171" }}>
              {fmtMoney(net)}
            </div>
          </div>
        </div>

        <Textarea
          data-testid="cash-commentaire"
          placeholder="Commentaire (optionnel)"
          value={f.commentaire}
          onChange={(e) => setF({ ...f, commentaire: e.target.value })}
          className="bg-[#09090b] border-[#27272a] mb-4"
        />

        <Button
          data-testid="submit-cash"
          onClick={submit}
          disabled={saving}
          className="w-full h-14 bg-[#fbbf24] hover:bg-[#f59e0b] text-black text-base font-semibold"
        >
          <FloppyDisk size={20} weight="fill" className="mr-2" />
          {saving ? "Enregistrement..." : existingForDate ? "Mettre à jour" : "Enregistrer le compte du jour"}
        </Button>
      </div>

      {/* Résumé du mois — visible uniquement côté Patron */}
      {isAdmin && (
        <>
          <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-display text-2xl font-medium tracking-tight capitalize">
              Résumé — {monthLabel}
            </h2>
            <div className="flex gap-2">
              <select
                data-testid="month-select"
                value={period.month}
                onChange={(e) => setPeriod({ ...period, month: Number(e.target.value) })}
                className="bg-[#121217] border border-[#27272a] text-white rounded-md px-3 py-2 h-10 text-sm"
              >
                {Array.from({ length: 12 }).map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2000, i, 1).toLocaleDateString("fr-FR", { month: "long" })}
                  </option>
                ))}
              </select>
              <Input
                data-testid="year-input"
                type="number"
                value={period.year}
                onChange={(e) => setPeriod({ ...period, year: Number(e.target.value) })}
                className="bg-[#121217] border-[#27272a] h-10 w-[100px]"
              />
            </div>
          </div>

          {monthly && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <SummaryKpi testId="m-cb" label="Total CB" value={fmtMoney(monthly.total_cb)} color="#60a5fa" />
                <SummaryKpi testId="m-cash" label="Total espèces" value={fmtMoney(monthly.total_cash)} color="#34d399" />
                <SummaryKpi testId="m-ca" label="Total CA" value={fmtMoney(monthly.total_ca)} color="#fbbf24" big />
                <SummaryKpi testId="m-charges" label="Total charges" value={fmtMoney(monthly.total_charges)} color="#f87171" />
                <SummaryKpi
                  testId="m-net"
                  label="Total net"
                  value={fmtMoney(monthly.total_net)}
                  color={monthly.total_net >= 0 ? "#34d399" : "#f87171"}
                  big
                />
              </div>

              <div className="label-xs mb-3">
                Détail par jour ({monthly.days_saisis} jour{monthly.days_saisis > 1 ? "s" : ""} saisi{monthly.days_saisis > 1 ? "s" : ""})
              </div>
              <DataTable
                columns={[
                  { title: "Date", render: (r) => fmtDate(r.date_jour) },
                  { title: "CB", align: "right", render: (r) => <span style={{ color: "#60a5fa" }}>{fmtMoney(r.cb_total)}</span> },
                  { title: "Espèces", align: "right", render: (r) => <span style={{ color: "#34d399" }}>{fmtMoney(r.cash_total)}</span> },
                  { title: "CA", align: "right", render: (r) => <b>{fmtMoney(r.total_ca)}</b> },
                  { title: "Charges", align: "right", render: (r) => <span style={{ color: "#f87171" }}>{fmtMoney(r.charges_total)}</span> },
                  { title: "Net", align: "right", render: (r) => <b style={{ color: r.total_net >= 0 ? "#34d399" : "#f87171" }}>{fmtMoney(r.total_net)}</b> },
                  {
                    title: "",
                    render: (r) => (
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onDateChange(r.date_jour)}
                          className="h-8 border-[#27272a] hover:bg-[#1a1a21] text-white"
                          data-testid={`edit-cash-${r.date_jour}`}
                        >
                          Modifier
                        </Button>
                        <DeleteButton
                          url={`/daily-cash/${r.id}`}
                          label={`le compte du ${fmtDate(r.date_jour)}`}
                          onDone={() => { loadMonthly(); loadExisting(f.date_jour); }}
                          testId={`delete-cash-${r.date_jour}`}
                        />
                      </div>
                    ),
                  },
                ]}
                rows={monthly.rows}
                rowKey="id"
                emptyText="Aucun compte saisi sur ce mois"
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

function BigNumericInput({ testId, icon: Icon, color, label, hint, value, onChange }) {
  return (
    <div className="surface p-4 !bg-[#09090b]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={20} color={color} weight="fill" />
          <div className="label-xs" style={{ color }}>{label}</div>
        </div>
        <div className="text-[10px] text-[#71717a]">{hint}</div>
      </div>
      <div className="flex items-center">
        <Input
          data-testid={testId}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="bg-transparent border-[#27272a] font-mono text-2xl md:text-3xl h-16 text-right pr-10"
        />
        <span className="-ml-8 text-xl text-[#71717a] font-mono pointer-events-none">€</span>
      </div>
    </div>
  );
}

function SummaryKpi({ testId, label, value, color = "#fff", big = false }) {
  return (
    <div className="surface p-4" data-testid={testId}>
      <div className="label-xs">{label}</div>
      <div
        className={`font-display font-light tracking-tight mt-2 ${big ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"}`}
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}
